/** Minimal shape needed to group leave rows; matches both SchedulerTypes and adminService ScheduleItem. */
export interface SeriesScheduleItem {
  id: string;
  type: string;
  date: string;
  endDate?: string | null;
  doctorId?: string | null;
  staffId?: string | null;
}

/**
 * A contiguous block of leave/MC schedule rows for one person.
 *
 * A multi-day leave can be stored two ways in this app (both exist in prod):
 *   1. a single range row  (date = start, end_date = end), or
 *   2. many single-day rows (one row per date, no end_date, no shared id).
 * This helper collapses either shape into one logical leave so the UI can
 * delete the whole thing in a single action instead of day-by-day.
 */
export interface LeaveSeries {
  /** Base row ids (no `_day_N` suffix) that make up the leave. */
  baseIds: string[];
  /** yyyy-MM-dd of the first day. */
  startDate: string;
  /** yyyy-MM-dd of the last day. */
  endDate: string;
  /** Inclusive number of calendar days the leave spans. */
  dayCount: number;
}

const stripDaySuffix = (id: string): string => id.split('_day_')[0];

const personKey = (item: SeriesScheduleItem): string => {
  if (item.doctorId) return `d:${item.doctorId}`;
  if (item.staffId) return `s:${item.staffId}`;
  return 'none';
};

/** Add `days` to a yyyy-MM-dd string via UTC (timezone-safe, no DST in SGT anyway). */
const addDaysStr = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const diffDays = (a: string, b: string): number => {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
};

/** All yyyy-MM-dd dates a single row covers (range rows expand, single-day rows are one). */
const coveredDates = (item: SeriesScheduleItem): string[] => {
  const start = item.date;
  const end = item.endDate && item.endDate !== item.date ? item.endDate : item.date;
  if (end <= start) return [start];
  const dates: string[] = [];
  for (let d = start; d <= end; d = addDaysStr(d, 1)) dates.push(d);
  return dates;
};

/**
 * Compute the contiguous same-person, same-type (leave/mc) block that contains
 * the `anchor` item, using every schedule row available to the view.
 *
 * `allItems` may contain virtual `_day_N` expansions; they are collapsed to their
 * base rows so each real row is counted once.
 */
export const getLeaveSeries = (
  anchor: SeriesScheduleItem,
  allItems: SeriesScheduleItem[],
): LeaveSeries => {
  const anchorBase = stripDaySuffix(anchor.id);

  // Collapse virtual/day rows to unique base rows.
  const byId = new Map<string, SeriesScheduleItem>();
  for (const it of allItems) {
    const bid = stripDaySuffix(it.id);
    if (!byId.has(bid)) byId.set(bid, { ...it, id: bid });
  }
  // Ensure the anchor's own base row is present even if not in the list.
  if (!byId.has(anchorBase)) byId.set(anchorBase, { ...anchor, id: anchorBase });

  const sameLeave = Array.from(byId.values()).filter(
    (it) => it.type === anchor.type && personKey(it) === personKey(anchor),
  );

  // Map each covered date to the set of base ids covering it.
  const dateToIds = new Map<string, Set<string>>();
  for (const it of sameLeave) {
    for (const d of coveredDates(it)) {
      if (!dateToIds.has(d)) dateToIds.set(d, new Set());
      dateToIds.get(d)!.add(it.id);
    }
  }

  const anchorDates = coveredDates({ ...anchor, id: anchorBase });
  let start = anchorDates[0];
  let end = anchorDates[anchorDates.length - 1];

  // Grow the span outward while adjacent days are also on leave.
  while (dateToIds.has(addDaysStr(start, -1))) start = addDaysStr(start, -1);
  while (dateToIds.has(addDaysStr(end, 1))) end = addDaysStr(end, 1);

  // Collect every base id whose covered dates fall inside [start, end].
  const ids = new Set<string>();
  for (let d = start; d <= end; d = addDaysStr(d, 1)) {
    const s = dateToIds.get(d);
    if (s) s.forEach((id) => ids.add(id));
  }
  // Anchor is always part of its own series.
  ids.add(anchorBase);

  return {
    baseIds: Array.from(ids),
    startDate: start,
    endDate: end,
    dayCount: diffDays(start, end) + 1,
  };
};
